import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import moment from 'moment';
import { InfoCircleOutlined } from '@ant-design/icons';
import { AdminGrid, AdminTable, CountryFlag, ActionButtons } from '@src/components/admin-grid';
import Button from '@src/components/button/Button';
import Spinner from '@src/components/spinner/Spinner';
import { useToast } from '@src/hooks/useToast';
import styles from './SentryTrackingManagement.module.scss';
import SentryTrackingModal from '@src/components/sentry-tracking/SentryTrackingModal';
import FilterSection from '@src/components/filter-section';
import Select from '@src/components/select/Select';
import Checkbox from '@src/components/checkbox/Checkbox';
import Tooltip from '@src/components/tooltip/Tooltip';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import NormalModal from '@src/components/modal/normal-modal/NormalModal';
import IconWarning from '@src/assets/icons/modal-warning.svg?react';
import { 
  GET_ALL_SENTRY_TRACKING, 
  ADD_SENTRY_TRACKING, 
  UPDATE_SENTRY_TRACKING, 
  REMOVE_SENTRY_TRACKING,
  GET_USERS,
  GET_ACCOUNTS,
  GET_SCHOOLS
} from '@src/components/sentry-tracking/sentryGraphQL';


const SentryTrackingManagement: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [trackingLevelFilter, setTrackingLevelFilter] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const { successToast, errorToast } = useToast();

  // Fetch tracking configurations
  const { data, loading, error, refetch } = useQuery(GET_ALL_SENTRY_TRACKING, {
    fetchPolicy: 'network-only',
  });

// These queries are commented out as they are not needed
// The backend should provide all necessary entity details
/*
const { data: usersData } = useQuery(GET_USERS, {
  variables: { input: {} }
});
const { data: accountsData } = useQuery(GET_ACCOUNTS);
const { data: schoolsData } = useQuery(GET_SCHOOLS);
*/

  // Mutations
  const [addSentryTracking] = useMutation(ADD_SENTRY_TRACKING);
  const [updateSentryTracking] = useMutation(UPDATE_SENTRY_TRACKING);
  const [removeSentryTracking] = useMutation(REMOVE_SENTRY_TRACKING);

  // Get entity name for display
  const getEntityName = (record: any) => {
    if (record.entityType === 'user') {
      // If we have the entityName from the backend, use it
      if (record.entityName) {
        return `${record.entityName} (${record.entityEmail || 'No Email'})
Account: ${record.accountName || 'No Account'}
Schools: ${record.schoolName || 'No Schools'}
Role: ${record.entityRole || 'Unknown'}`;
      }
      
      // Since we've commented out the queries, just return the entity ID
      return record.entityId;
    } else if (record.entityType === 'account') {
      // If we have the entityName from the backend, use it
      if (record.entityName) {
        return record.entityName;
      }
      
      // Since we've commented out the queries, just return the entity ID
      return record.entityId;
    } else if (record.entityType === 'school') {
      // If we have the entityName from the backend, use it
      if (record.entityName) {
        return record.entityName;
      }
      
      // Since we've commented out the queries, just return the entity ID
      return record.entityId;
    }
    return record.entityId;
  };

  // Filter tracking configurations based on active tab and filters
  const filteredData = data?.sentryTrackingConfigurations?.filter((config: any) => {
    // Filter by tab
    if (activeTab !== 'all' && config.entityType.toLowerCase() !== activeTab) {
      return false;
    }
    
    // Filter by entity type
    if (entityTypeFilter && config.entityType !== entityTypeFilter) {
      return false;
    }
    
    // Filter by tracking level
    if (trackingLevelFilter && config.trackingLevel !== trackingLevelFilter) {
      return false;
    }
    
    // Filter by active status
    if (showActiveOnly && !config.isActive) {
      return false;
    }
    
    // Filter by search term (entity name)
    if (search) {
      const entityName = getEntityName(config).toLowerCase();
      if (!entityName.includes(search.toLowerCase())) {
        return false;
      }
    }
    
    return true;
  }) || [];
  
  // Pagination
  const paginatedData = filteredData.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalItems = filteredData.length;

  // Handle opening the edit modal
  const handleOpenEditModal = (record: any) => {
    setEditingRecord(record);
    setIsModalVisible(true);
  };
  
  // Handle closing the modal
  const handleCloseModal = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
  };
  
  // Handle successful tracking update
  const handleTrackingSuccess = () => {
    refetch();
  };

  // Handle removing tracking
  const handleRemoveTracking = async () => {
    if (!editingRecord?.id) return;
    
    try {
      await removeSentryTracking({
        variables: { id: editingRecord.id },
      });
      
      successToast('Sentry tracking removed successfully');
      setConfirmDeleteVisible(false);
      setEditingRecord(null);
      refetch();
    } catch (error) {
      errorToast('Failed to remove Sentry tracking: ' + (error as Error).message);
    }
  };

  // Open delete confirmation modal
  const handleConfirmDelete = (record: any) => {
    setEditingRecord(record);
    setConfirmDeleteVisible(true);
  };
  
  // Close delete confirmation modal
  const handleCancelDelete = () => {
    setConfirmDeleteVisible(false);
    setEditingRecord(null);
  };


  // Handle search change
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };
  
  // Handle filter changes
  const handleEntityTypeChange = (option: any) => {
    setEntityTypeFilter(option.value);
    setCurrentPage(1);
  };
  
  const handleTrackingLevelChange = (option: any) => {
    setTrackingLevelFilter(option.value);
    setCurrentPage(1);
  };
  
  const handleActiveOnlyChange = (value: boolean) => {
    setShowActiveOnly(value);
    setCurrentPage(1);
  };
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  const handleResetFilters = () => {
    setSearch('');
    setEntityTypeFilter('');
    setTrackingLevelFilter('');
    setShowActiveOnly(true);
    setActiveTab('all');
    setCurrentPage(1);
  };
  
  // Table header columns
  const headerColumns = [
    {
      title: 'Entity Type',
      sort: {
        key: 'entityType',
        asc: (a: any, b: any) => a.entityType.localeCompare(b.entityType),
        desc: (a: any, b: any) => b.entityType.localeCompare(a.entityType)
      }
    },
    {
      title: 'Entity',
      sort: {
        key: 'entity',
        asc: (a: any, b: any) => getEntityName(a).localeCompare(getEntityName(b)),
        desc: (a: any, b: any) => getEntityName(b).localeCompare(getEntityName(a))
      }
    },
    {
      title: 'Country',
      center: true
    },
    {
      title: 'Tracking Level',
      sort: {
        key: 'trackingLevel',
        asc: (a: any, b: any) => a.trackingLevel.localeCompare(b.trackingLevel),
        desc: (a: any, b: any) => b.trackingLevel.localeCompare(a.trackingLevel)
      }
    },
    {
      title: 'Status'
    },
    {
      title: 'Start Date',
      sort: {
        key: 'startDate',
        asc: (a: any, b: any) => parseInt(a.startDate, 10) - parseInt(b.startDate, 10),
        desc: (a: any, b: any) => parseInt(b.startDate, 10) - parseInt(a.startDate, 10)
      }
    },
    {
      title: 'End Date',
      sort: {
        key: 'endDate',
        asc: (a: any, b: any) => {
          if (!a.endDate && !b.endDate) return 0;
          if (!a.endDate) return 1;
          if (!b.endDate) return -1;
          return parseInt(a.endDate, 10) - parseInt(b.endDate, 10);
        },
        desc: (a: any, b: any) => {
          if (!a.endDate && !b.endDate) return 0;
          if (!a.endDate) return -1;
          if (!b.endDate) return 1;
          return parseInt(b.endDate, 10) - parseInt(a.endDate, 10);
        }
      }
    },
    {
      title: 'Actions',
      center: true
    }
  ];
  
  // Table body columns
  const bodyColumns = [
    {
      key: (row: any) => (
        <div className={styles.entityType}>
          {row.entityType.charAt(0).toUpperCase() + row.entityType.slice(1)}
        </div>
      )
    },
    {
      key: (row: any) => {
        const entityInfo = getEntityName(row);
        
        if (row.entityType === 'user') {
          // For users, display structured information with name as the main identifier
          const lines = entityInfo.split('\n');
          return (
            <div className={styles.entityName}>
              <div><strong>{row.entityName || lines[0]}</strong></div>
              <div>{lines[1]}</div>
              <div>{lines[2]}</div>
              <div>{lines[3]}</div>
            </div>
          );
        } else {
          // For other entity types, display as before
          return (
            <div className={styles.entityName} title={entityInfo}>
              {entityInfo}
            </div>
          );
        }
      }
    },
    {
      key: (row: any) => (
        <div style={{display: 'flex', justifyContent: 'center'}}>
          <CountryFlag countryCode={row.entityId.substring(0, 2)} />
        </div>
      )
    },
    {
      key: (row: any) => (
        <div className={styles.trackingLevel}>
          <span className={`${styles.badge} ${styles[`badge${row.trackingLevel.charAt(0).toUpperCase() + row.trackingLevel.slice(1)}`]}`}>
            {row.trackingLevel.charAt(0).toUpperCase() + row.trackingLevel.slice(1)}
          </span>
        </div>
      )
    },
    {
      key: (row: any) => (
        <div className={styles.status}>
          {row.isActive ? (
            <span className={`${styles.badge} ${styles.badgeActive}`}>Active</span>
          ) : (
            <span className={`${styles.badge} ${styles.badgeInactive}`}>Inactive</span>
          )}
        </div>
      )
    },
    {
      key: (row: any) => (
        <div className={styles.date}>
          {moment(parseInt(row.startDate, 10)).format('YYYY-MM-DD')}
        </div>
      )
    },
    {
      key: (row: any) => (
        <div className={styles.date}>
          {row.endDate ? moment(parseInt(row.endDate, 10)).format('YYYY-MM-DD') : 'No end date'}
        </div>
      )
    },
    {
      key: (row: any, column, options) => (
        <div className={styles.actions}>
          <ActionButtons
            tooltipPosition="top"
            onEdit={() => handleOpenEditModal(row)}
            editTooltip="Edit tracking configuration"
            onDelete={() => handleConfirmDelete(row)}
            deleteTooltip="Remove tracking configuration"
            showView={false}
            showCopy={false}
            isRowHovered={options?.isRowHovered}
          />
        </div>
      )
    }
  ];

  // Entity type filter options
  const entityTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'user', label: 'User' },
    { value: 'account', label: 'Account' },
    { value: 'school', label: 'School' }
  ];
  
  // Tracking level filter options
  const trackingLevelOptions = [
    { value: '', label: 'All Levels' },
    { value: 'basic', label: 'Basic' },
    { value: 'detailed', label: 'Detailed' },
    { value: 'full', label: 'Full' }
  ];
  
  // Tab options
  const tabOptions = [
    { key: 'all', label: 'All' },
    { key: 'user', label: 'Users' },
    { key: 'account', label: 'Accounts' },
    { key: 'school', label: 'Schools' }
  ];
  
  // Filter section component
  const filterSection = (
    <FilterSection
      searchValue={search}
      onSearchChange={handleSearchChange}
      filters={[
        {
          value: entityTypeFilter,
          onChange: handleEntityTypeChange,
          label: 'Entity Type',
          options: entityTypeOptions,
          showSearch: false
        },
        {
          value: trackingLevelFilter,
          onChange: handleTrackingLevelChange,
          label: 'Tracking Level',
          options: trackingLevelOptions,
          showSearch: false
        }
      ]}
      advancedFilters={
        <Checkbox
          id="activeOnly"
          checked={showActiveOnly}
          onChange={handleActiveOnlyChange}
          label="Show Active Only"
        />
      }
      onReset={handleResetFilters}
      showDivider={false}
    />
  );

  return (
    <AdminGrid
      title="Sentry Tracking Management"
      filterSection={filterSection}
      resultsCount={totalItems}
      resultsCountLabel="Tracking Configurations"
    >
      <div className={styles.infoBox}>
        <div className={styles.infoBoxColumns}>
          <div className={styles.infoBoxColumn}>
            <h3><InfoCircleOutlined /> About Sentry Tracking</h3>
            <p>
              Sentry tracking allows you to monitor errors and performance for specific users, accounts, or schools.
              By default, 3% of production users are randomly sampled. To add specific entities for tracking, use the options in the Users or Accounts pages.
            </p>
          </div>
          <div className={styles.infoBoxColumn}>
            <div className={styles.trackingLevelsInfo}>
              <h3>Tracking Levels</h3>
              <ul>
                <li><span className={`${styles.badge} ${styles.badgeBasic}`}>Basic</span> Captures errors and basic performance metrics</li>
                <li><span className={`${styles.badge} ${styles.badgeDetailed}`}>Detailed</span> Adds more context and breadcrumbs to errors</li>
                <li><span className={`${styles.badge} ${styles.badgeFull}`}>Full</span> Captures session replays and detailed performance metrics</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className={styles.tabsContainer}>
        {tabOptions.map(tab => (
          <div 
            key={tab.key} 
            className={`${styles.tab} ${activeTab === tab.key ? styles.activeTab : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </div>
        ))}
      </div>
      
      {loading ? (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      ) : (
        <AdminTable
          header={headerColumns}
          body={bodyColumns}
          data={paginatedData}
          loading={loading}
          currentPage={currentPage}
          perPage={perPage}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          color="green"
          noItemsText="No tracking configurations found."
        />
      )}
      
      {/* Edit Tracking Modal */}
      {isModalVisible && (
        <SentryTrackingModal
          visible={isModalVisible}
          onClose={handleCloseModal}
          entityId={editingRecord?.entityId || ''}
          entityType={editingRecord?.entityType || 'user'}
          entityName={getEntityName(editingRecord)}
          onSuccess={handleTrackingSuccess}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      {confirmDeleteVisible && editingRecord && (
        <NormalModal
          logoIcon={<IconWarning />}
          title="Remove Tracking"
          open={confirmDeleteVisible}
          onClose={handleCancelDelete}
          description={`Are you sure you want to remove Sentry tracking for ${getEntityName(editingRecord)}?`}
          leftButton={
            <Button
              size="small"
              color="green"
              onClick={handleCancelDelete}
              label="CANCEL"
            />
          }
          rightButton={
            <Button
              size="small"
              color="red"
              onClick={handleRemoveTracking}
              label="REMOVE TRACKING"
            />
          }
        />
      )}
    </AdminGrid>
  );
};

export default SentryTrackingManagement;
